import { useEffect, useState, useCallback, useMemo, useRef, useSyncExternalStore } from 'react';
import { Plus, ExternalLink, ChevronsRight, Settings, Info } from '../../icons';
import { layerAlert, layerConfirm } from '../../components/LayerPopup/LayerPopup';
import {
  listProjects, listTasks, patchTask, deleteTask,
  type TaskProjectDto, type TaskDto, type TaskStatus,
} from '../../api/tasks';
import { listTeams, listTeamMembers, type TeamDto, type TeamMemberDto } from '../../api/project-teams';
import { ProjectTopBar } from './ProjectTopBar';
import { ProjectSidePanel } from './ProjectSidePanel';
import { TASK_PROJECT_SIDEPANEL } from '../../lib/task-shell';
import {
  resolveInitialScope, projectsInScope, pickProjectForScope,
  syncScopeUrl, scopeFromQueryTeam, type TaskScope,
} from './task-scope';
import { ProjectCreateSimpleModal } from '../ProjectPage/ProjectCreateSimpleModal';
import { ProjectSettingsModal } from '../ProjectPage/ProjectSettingsModal';
import { confirmAndDeleteProject, isProjectCreator } from '../ProjectPage/confirmDeleteProject';
import { TaskDetailModal } from './TaskDetailModal';
import { KanbanBoard } from './KanbanBoard';
// AW-002-02 (2026-08-18) — 목록보기는 상태 그룹 + 커스텀 필드 표(TaskTableView)로 그린다.
// 기존 TaskListView(평면 표)는 보존 — 롤백 시 이 자리만 되돌리면 된다.
import { TaskTableView } from './TaskTableView';
import { TaskCreateModal } from './TaskCreateModal';
// 2026-08-18 시안 — 헤더 팀 배지 · 보드 툴바 · 하단 진행현황 도크(TeamGantt 를 감싼다).
import { TeamMemberBadge } from './TeamMemberBadge';
import { TaskToolbar } from './TaskToolbar';
import { TeamProgressDock } from './TeamProgressDock';
// 화면구성도 V0.1 슬라이드 9 (2026-08-18) — 우측 프로젝트 정보(개요·멤버·활동 내역).
import { ProjectInfoPanel } from './ProjectInfoPanel';
import { EMPTY_TASK_FILTER, filterTasks, type TaskFilterState, type TaskViewMode } from './task-filter';
import { useAuthStore } from '../../store/authStore';
import { useModalLeave } from '../../hooks/useModalLeave';
import { useBodyScrollLock } from '../../hooks/useBodyScrollLock';
import { tTask, getTaskLang, subscribeTaskLang } from '../../lib/task-i18n';
import { tProject } from '../../lib/project-i18n';
import './TaskPage.css';

/**
 * TaskPage (P3-TASK-FE-2) — 시안 line 1882~ 매핑.
 *
 * 구조:
 *   - 좌측: ProjectSidebar (프로젝트 list + 새 프로젝트).
 *   - 우측: 헤더 (프로젝트 이름 + 진행률 + Kanban/List 토글 + 새 task) + KanbanBoard / TaskListView.
 */
export function TaskPage() {
  useSyncExternalStore(subscribeTaskLang, getTaskLang, getTaskLang);
  const [projects, setProjects] = useState<TaskProjectDto[]>([]);
  // 좌측 '내 프로젝트' 패널 접힘(2026-07-24) — 캘린더 사이드바와 같은 폭 전이 접기.
  //
  // 2026-08-19: 접힌 상태를 기본값으로 바꿨다(사용자 요청) — 보드를 보러 오는 화면이라
  // 목록이 248px 를 늘 먹고 있을 이유가 없다는 판단이었다.
  //
  // 2026-09-16: **다시 펼친 상태가 기본**이다(사용자 지시 — "디폴트로 접히지 않고 펼쳐지도록
  // 고정"). 매번 » 를 눌러 여는 손이 더 들었다는 것이 되돌린 이유다. 접기 버튼(«)은 그대로
  // 남겨 둔다 — 보드를 넓게 쓰고 싶을 때는 여전히 접을 수 있고, 접은 상태는 저장하지 않으므로
  // 다시 들어오면 **항상 펼친 채로** 시작한다(그게 이 지시의 '고정').
  const [sideCollapsed, setSideCollapsed] = useState(false);
  // 팀 스코프(design-20260724 D-1) — null = 아직 결정 전(팀·프로젝트 로드 대기).
  const [scope, setScope] = useState<TaskScope | null>(null);
  const [activeProjectId, setActiveProjectId] = useState<string | null>(null);
  const [tasks, setTasks] = useState<TaskDto[]>([]);
  /** 현재 tasks 가 어느 프로젝트의 것인지 — 프로젝트 전환 중 잔상 차단용(refreshTasks 참조). */
  const [tasksProjectId, setTasksProjectId] = useState<string | null>(null);
  const [creating, setCreating] = useState<{ initialStatus: TaskStatus } | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // v2.7 — 상단 카드 바의 [새 프로젝트] → 상세 생성 모달(v2.6). 팀 목록은 셀렉터용.
  const [teams, setTeams] = useState<TeamDto[]>([]);
  const [creatingProject, setCreatingProject] = useState(false);
  // 2026-09-11 — 빈 스코프 안내의 [새 프로젝트 만들기] 로 열었는지. 그때만 지금 보던 스페이스를
  // 생성 모달의 위치 기본값으로 넘긴다(좌측 패널의 [새 프로젝트] 는 종전대로 개인이 기본).
  const [createFromEmptyScope, setCreateFromEmptyScope] = useState(false);
  // 프로젝트 설정 모달(수정·위임·삭제) — 헤더 [설정] 버튼으로 연다.
  const [settingsOpen, setSettingsOpen] = useState(false);
  // 2026-08-18 시안 — 보드 툴바(보기 전환 · 필터 · 업무 검색). 기본은 칸반(종전 단일 뷰와 동일).
  const [view, setView] = useState<TaskViewMode>('kanban');
  // 우측 프로젝트 정보 패널 — 헤더 ⓘ 로 연다.
  const [infoOpen, setInfoOpen] = useState(false);
  const [filter, setFilter] = useState<TaskFilterState>(EMPTY_TASK_FILTER);
  const authUser = useAuthStore((s) => s.user);
  const myId = authUser?.id != null ? String(authUser.id) : '';

  const {
    shouldRender: shouldRenderCreate,
    isLeaving: isCreateLeaving,
    latched: creatingSnap,
  } = useModalLeave(creating, 200);
  const projectModal = useModalLeave(creatingProject || null, 200);
  // v2.7.2 — 칸반 카드 클릭 → 태스크 상세 레이어팝업
  const [detailTask, setDetailTask] = useState<TaskDto | null>(null);
  /**
   * 추가 정보(커스텀 필드) 변경 횟수 (2026-08-21).
   * 상세에서 값을 고치면 이 수가 늘고, 목록보기 표가 그걸 보고 값을 다시 읽는다.
   * 값이 TaskDto 에 실려 오지 않아 tasks 갱신만으로는 표가 모르기 때문이다.
   */
  const [cfVersion, setCfVersion] = useState(0);
  /**
   * 칸반 카드의 담당자 이름 표시용 팀원 목록 (2026-08-31).
   * 목록보기(TaskTableView)와 상세(TaskDetailModal)는 각자 같은 함수를 부르고, 그 함수는
   * 팀 단위 캐시를 들고 있어 호출이 늘어도 요청은 늘지 않는다.
   */
  const [boardMembers, setBoardMembers] = useState<TeamMemberDto[]>([]);
  const detailModal = useModalLeave(detailTask, 200);
  useBodyScrollLock(shouldRenderCreate || projectModal.shouldRender || detailModal.shouldRender);

  useEffect(() => {
    void listTeams().then(setTeams).catch(() => setTeams([]));
  }, []);

  // project-menu-detailing (2026-06-10) — /project 에서 ?project=<id> 로 진입 시 초기 선택.
  // Router 컨텍스트 불요한 window.location 읽기 (기존 TaskPage 테스트의 Router-free 렌더 계약 보존).
  // 쿼리 미지정 시 동작 불변 — 기존 첫 프로젝트 기본 선택 뒤에 덮어써서 적용.
  useEffect(() => {
    const qp = new URLSearchParams(window.location.search).get('project');
    if (!qp) return;
    if (projects.some((p) => p.id === qp)) {
      setActiveProjectId(qp);
    }
  }, [projects]);

  /**
   * 초기 팀 스코프 결정 (design D-1) — 프로젝트 메뉴가 파라미터 없이 /task 를 열기 때문에
   * "무엇을 기본으로 볼지"를 규칙으로 정한다: ?project= → ?team= → **내 팀 전체**(기본)
   * → (소속 팀 없음) 개인. 팀·프로젝트가 모두 로드된 뒤 한 번만 정한다.
   * 지난 선택을 기억하지 않는 이유 — 기본값이 항상 '내 팀 전체' 여야 한다는 결정(2026-07-24).
   * ⚠ Router 없이 렌더되는 기존 계약 유지 — window.location 만 읽는다.
   */
  useEffect(() => {
    if (scope !== null || projects.length === 0) return;
    const qs = new URLSearchParams(window.location.search);
    const fromQuery = scopeFromQueryTeam(qs.get('team'));
    if (fromQuery?.kind === 'personal') { setScope({ kind: 'personal' }); return; }
    setScope(resolveInitialScope({
      teams,
      projects,
      queryTeamId: fromQuery?.kind === 'team' ? fromQuery.teamId : null,
      queryProjectId: qs.get('project'),
    }));
  }, [teams, projects, scope]);

  /**
   * 좌측 카드의 진척률을 **보드와 같은 시각으로** 맞춘다 (2026-09-15).
   *
   * `projects` 의 taskCount/doneTaskCount 는 화면에 들어올 때 한 번 받은 서버 집계다. 그래서
   * 카드를 [완료] 로 옮겨도 좌측 진척률은 들어올 때 값에 머물렀다 — 보드는 완료 4건인데 카드는
   * `1/4 · 25%` (신고 2026-09-15). 목록을 다시 받아오기 전까지 두 숫자가 계속 어긋났다.
   *
   * 지금 **보고 있는 프로젝트만** 화면에 담긴 tasks 로 다시 센다. 기준은 서버와 같다 —
   * 분모 = 삭제되지 않은 전체(`listTasks` 응답이 곧 그것), 분자 = `status === 'DONE'`.
   * 그래서 새로고침하면 값이 튀지 않는다. 다른 프로젝트 카드는 서버 값 그대로.
   *
   * 담긴 목록이 이 프로젝트 것일 때만 덮어쓴다(`tasksProjectId`) — 프로젝트 전환 중에
   * 조회가 끝나기 전 `0/0 · 0%` 가 잠깐 비치지 않게. 덮어쓸 값이 숫자 두 개뿐이라
   * 배열 참조는 **값이 실제로 바뀔 때만** 새로 만들어진다(아래 memo 의 경고 참조).
   */
  const liveCountsFor = tasksProjectId === activeProjectId ? activeProjectId : null;
  const liveTaskCount = liveCountsFor === null ? 0 : tasks.length;
  const liveDoneCount = useMemo(
    () => (liveCountsFor === null ? 0 : tasks.filter((t) => t.status === 'DONE').length),
    [liveCountsFor, tasks],
  );
  const liveProjects = useMemo(
    () => (liveCountsFor === null
      ? projects
      : projects.map((p) => (p.id === liveCountsFor
        ? { ...p, taskCount: liveTaskCount, doneTaskCount: liveDoneCount }
        : p))),
    [projects, liveCountsFor, liveTaskCount, liveDoneCount],
  );

  // 스코프 안에서만 고르고, 주소를 맞춘다.
  // ⚠ useMemo 필수(2026-07-25 진단) — 렌더마다 새 배열을 만들면 이 값을 prop 으로 받는
  //   ProjectSidePanel 의 effect(deps=[projects])가 **리렌더마다 재발화**해 팀별 멤버 조회가
  //   반복된다(실측: 카드 전환 1회당 +3회, 화면 진입 20회 중 8회가 그 중복).
  const scopedProjects = useMemo(
    () => (scope ? projectsInScope(liveProjects, scope) : liveProjects),
    [liveProjects, scope],
  );
  // ★ 선택을 옮기는 곳이 둘이다 — 여기와 deleteProject(아래). 겹쳐 보이지만 «덮는 창이 다르다»:
  //   이 효과는 바로 아래 'if (!scope) return;' 때문에 ⭐ scope 가 정해지기 «전»에는 돌지 않는다.
  //   그 창을 덮는 것은 deleteProject 쪽 한 줄이다 — 그 줄의 주석을 먼저 읽을 것.
  useEffect(() => {
    if (!scope) return;
    const next = pickProjectForScope(projectsInScope(projects, scope), activeProjectId);
    if (next !== activeProjectId) setActiveProjectId(next);
    syncScopeUrl(scope, next);
  }, [scope, projects, activeProjectId]);

  // 프로젝트 list 로드.
  useEffect(() => {
    void (async () => {
      try {
        const ps = await listProjects();
        setProjects(ps);
        if (ps.length > 0) {
          // ⚠ 이미 고른 프로젝트는 덮어쓰지 않는다(2026-09-14) — 이 클로저의 activeProjectId 는
          //   마운트 시점 값(null)이라 낡았다. 목록 응답이 두 번 오면(StrictMode effect 이중 실행 등)
          //   늦은 응답이 ?project= 로 고른 선택을 '전체 첫 프로젝트'로 되돌리고, 그 프로젝트가 다른
          //   스페이스면 스코프 보정이 **스코프 안 첫 프로젝트**로 바꿔 엉뚱한 프로젝트가 열렸다
          //   (신고: axi → [레거시 정리] 를 눌렀는데 'asddsasad' 가 열림).
          //   첫 선택도 ?project= 가 목록에 있으면 그것부터 — 다른 프로젝트가 잠깐 비치지 않게.
          const qp = new URLSearchParams(window.location.search).get('project');
          const initial = qp && ps.some((x) => x.id === qp) ? qp : ps[0]!.id;
          setActiveProjectId((curr) => curr ?? initial);
        }
      } catch (err) {
        setError((err as Error)?.message ?? tTask('task.err_fetch_projects'));
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // active 프로젝트 변경 → tasks 재조회.
  //
  // ⚠ 프로젝트를 빠르게 연달아 누르면 요청이 겹친다. 취소·순번 가드가 없으면 **늦게 도착한
  // 이전 프로젝트의 응답이 현재 프로젝트의 목록을 덮어써**, 선택한 프로젝트와 다른 프로젝트의
  // 태스크가 화면에 남는다(실제 신고: '알파'(0건)를 눌렀는데 '살생물'(4건) 태스크가 표시).
  // 두 겹으로 막는다.
  //   ① reqSeq — 최신 요청이 아니면 응답을 폐기(out-of-order 응답 차단).
  //   ② tasksProjectId — 지금 담긴 목록이 어느 프로젝트 것인지 태깅해, 로딩 중에 이전
  //      프로젝트의 태스크가 잔상으로 보이지 않게 한다(아래 visibleTasks).
  const reqSeq = useRef(0);
  const refreshTasks = useCallback(async () => {
    if (!activeProjectId) {
      reqSeq.current += 1;
      setTasks([]);
      setTasksProjectId(null);
      return;
    }
    const requestedFor = activeProjectId;
    reqSeq.current += 1;
    const seq = reqSeq.current;
    setLoading(true);
    setError(null);
    try {
      const ts = await listTasks(requestedFor);
      if (seq !== reqSeq.current) return; // 더 최신 요청이 있다 — 이 응답은 버린다.
      setTasks(ts);
      setTasksProjectId(requestedFor);
    } catch (err) {
      if (seq !== reqSeq.current) return;
      setError((err as Error)?.message ?? tTask('task.err_fetch_tasks'));
    } finally {
      if (seq === reqSeq.current) setLoading(false);
    }
  }, [activeProjectId]);

  useEffect(() => {
    void refreshTasks();
  }, [refreshTasks]);

  /**
   * Kanban drag-drop — task.status 변경. optimistic update + rollback.
   * Race: 빠른 연속 drag 시 마지막 patch 만 유효하지만 PATCH 응답 순서가 비결정이므로
   * 본 사이클은 단순 sequential await (후속: 큐잉).
   */
  async function handleStatusChange(taskId: string, newStatus: TaskStatus) {
    const prev = tasks;
    // optimistic.
    setTasks((curr) => curr.map((t) => (t.id === taskId ? { ...t, status: newStatus } : t)));
    try {
      const updated = await patchTask(taskId, { status: newStatus });
      setTasks((curr) => curr.map((t) => (t.id === taskId ? updated : t)));
    } catch (err) {
      // rollback.
      setTasks(prev);
      // 판 7 (2026-10-13) — 완료 승인 게이트에 막힌 것은 «실패»가 아니라 «갈 길이 다르다»를 알린다.
      //   (서버가 403 TASK_APPROVAL_REQUIRED 를 준다 — 승인을 켠 프로젝트 · 승인권 없는 사람 · DONE 으로)
      const code = (err as { response?: { data?: { code?: string } } })?.response?.data?.code;
      if (code === 'TASK_APPROVAL_REQUIRED') {
        void layerAlert(tTask('task.err.approval_required'));
        return;
      }
      void layerAlert(tTask('task.err.status_change', { msg: (err as Error)?.message ?? '' }));
    }
  }

  // 진척률이 살아 있는 쪽을 쓴다 — 헤더 ⓘ 정보 패널·설정 모달도 좌측 카드와 같은 숫자를 본다.
  const activeProject = liveProjects.find((p) => p.id === activeProjectId) ?? null;

  /**
   * 칸반 카드의 [🗑] 노출 조건 (2026-09-02) — 서버 규칙을 **그대로** 옮긴다:
   * `tasks.routes.ts` DELETE 는 `canDeleteTaskByRole(MANAGER+)` **또는** 본인 작성이면 통과한다.
   * 눌러 봐야 403 이 나는 버튼을 세우지 않으려고 화면에서도 같은 두 갈래로 가린다.
   *
   * `myRole` 은 목록 응답의 additive 필드라 옛 서버 응답에는 없다 — 없으면 role 갈래를 포기하고
   * 작성자 갈래만 본다(가려서 못 지우는 쪽이, 눌러도 안 되는 버튼보다 낫다).
   * 상세 팝업의 [삭제] 는 작성자만 보이는데(canEdit), 그건 '수정 권한' 을 재사용한 것이라
   * 서버보다 좁다. 카드는 서버 기준을 쓴다.
   */
  const canDeleteCard = useCallback(
    (t: TaskDto) => t.createdBy === myId
      || activeProject?.myRole === 'OWNER' || activeProject?.myRole === 'MANAGER',
    [myId, activeProject?.myRole],
  );

  /**
   * 카드에서 바로 삭제 (2026-09-02). 확인을 먼저 받는다 — 카드는 한 번 눌러 지우는 길이라
   * 상세 팝업의 [삭제] 보다 잘못 누르기 쉽다. 그래서 확인창에 '되돌릴 수 없다' 를 덧붙인다.
   *
   * 지운 뒤 **목록을 다시 읽지 않는다** — 그 카드만 빼면 칸반이 통째로 깜빡이지 않고,
   * 진행 중인 드래그·스크롤도 살아 있다(내 업무 행 [🗑] 과 같은 기준).
   * 열려 있던 상세가 방금 지운 업무면 함께 닫는다.
   */
  async function deleteOneTask(t: TaskDto) {
    const ok = await layerConfirm(
      `${tTask('task.detail.delete_confirm', { title: t.title })}\n${tTask('task.card.delete_irreversible')}`,
      { danger: true },
    );
    if (!ok) return;
    try {
      await deleteTask(t.id);
      setTasks((curr) => curr.filter((x) => x.id !== t.id));
      setDetailTask((curr) => (curr?.id === t.id ? null : curr));
    } catch (err) {
      const code = (err as { response?: { data?: { code?: string } } })?.response?.data?.code;
      void layerAlert(code === 'FORBIDDEN'
        ? tTask('task.card.delete_err_forbidden')
        : tTask('task.detail.err_delete'));
    }
  }

  // 화면에 그릴 태스크 — 담긴 목록이 지금 선택한 프로젝트의 것일 때만 보여준다.
  // 전환 직후(조회 진행 중)에는 빈 보드가 되어 이전 프로젝트의 태스크가 남지 않는다.
  const visibleTasks = tasksProjectId === activeProjectId ? tasks : [];

  // 2026-08-18 — 툴바의 필터·검색을 적용한 집합. 칸반/목록 두 뷰가 같은 결과를 본다.
  // 하단 진행현황 도크는 "프로젝트 전체" 현황이라 필터 전 목록(visibleTasks)을 쓴다.
  const shownTasks = useMemo(
    () => filterTasks(visibleTasks, filter, myId),
    [visibleTasks, filter, myId],
  );
  // 프로젝트를 바꾸면 이전 프로젝트에 걸어 둔 필터·검색어가 남지 않게 한다.
  useEffect(() => { setFilter(EMPTY_TASK_FILTER); }, [activeProjectId]);

  /*
   * 칸반 카드가 담당자 이름을 쓰려면 팀원 목록이 필요하다 (2026-08-31).
   * 프로젝트가 팀에 속하지 않으면(개인 프로젝트) 목록을 비워 둔다 — 카드는 인원수로 물러선다.
   */
  const boardTeamId = activeProject?.teamId ?? null;
  useEffect(() => {
    if (!boardTeamId) { setBoardMembers([]); return; }
    let alive = true;
    void listTeamMembers(boardTeamId)
      .then((ms) => { if (alive) setBoardMembers(ms); })
      .catch(() => { if (alive) setBoardMembers([]); });
    return () => { alive = false; };
  }, [boardTeamId]);

  // 프로젝트 삭제 — 상단 바/좌측 패널 양쪽 경로가 같은 플로우를 쓴다(기존 v2.8.5 동작 보존).
  //
  // ⛔⛔ 아래 'if (activeProjectId === p.id) setActiveProjectId(...)' 를 ★ 「위 스코프 효과와
  //   중복이니까」로 지우지 마십시오 (2026-10-02 · 재서 적음 · 사용자 결정 목록에서 내린 항목).
  //
  // ⭐ 겹쳐 보이지만 «덮는 창이 다릅니다»:
  //   · 스코프 효과(위, pickProjectForScope)는 'if (!scope) return;' 이라 ⇒ ★ scope 가 정해지기
  //     «전»에는 아예 돌지 않습니다. scope 를 넣는 효과는 위에 따로 있고 그 관문이
  //     'if (scope !== null || projects.length === 0) return;' 입니다 ⇒ ★ 프로젝트가 «온 뒤에»
  //     비로소 정해집니다. 그 사이가 창입니다 — 줄은 이미 그려져 있고 scope 는 아직 null 입니다.
  //   · 이 핸들러는 ⇒ ★ 그 창에서도 돕니다. 지우는 단추는 onDelete 로 «조건 없이» 내려가므로
  //     (아래 ProjectSidePanel · 상단 바 양쪽) scope 와 무관하게 눌릴 수 있습니다.
  //
  // ⚠ 그 창은 resolveInitialScope 의 반환이 «널 아님»(task-scope.ts ': TaskScope')이라
  //   ⇒ ★ 프로젝트가 오면 곧 정해지는 «순간»이고 머무르지 않습니다. ⇒ ⛔ 그래서 ★ 시험이 그 창을
  //   못 잽니다(taskpage-delete-contract.test.tsx 는 scope 가 «정해진 뒤»만 잡습니다).
  //   ⭐ 「시험이 못 잰다」는 것이 ★ «그 창이 없다»는 뜻이 아닙니다 — 재는 법이 없다는 뜻입니다.
  //
  // ⇒ 그래서 한 곳으로 모으지 않고 «둘 다» 둡니다. 모으려면 scope 가 늘 정해져 있음을 «먼저»
  //   잠그고(그 효과의 조건을 없애고) 그 뒤에 이 줄을 지우십시오 — 순서를 뒤집으면 그 순간에
  //   지운 프로젝트가 선택으로 남습니다.
  const deleteProject = (p: TaskProjectDto) => {
    void confirmAndDeleteProject(p).then((deleted) => {
      if (!deleted) return;
      setProjects((prev) => {
        const next = prev.filter((x) => x.id !== p.id);
        if (activeProjectId === p.id) setActiveProjectId(next[0]?.id ?? null);
        return next;
      });
    });
  };

  // 프로젝트 설정(수정·위임·삭제) — 생성자 본인에게만 노출.
  const canOpenSettings = activeProject != null && isProjectCreator(activeProject);
  // 설정 저장 결과를 목록에 반영. PATCH 응답에는 목록 전용 additive 필드(doneTaskCount)가
  // 없으므로 기존 항목 위에 병합해 표시 정보가 사라지지 않게 한다.
  function applyProjectPatch(updated: TaskProjectDto) {
    setProjects((prev) => prev.map((x) => (x.id === updated.id ? { ...x, ...updated } : x)));
    setSettingsOpen(false);
  }
  function onProjectDeletedFromSettings() {
    const id = activeProject?.id;
    setSettingsOpen(false);
    if (!id) return;
    setProjects((prev) => {
      const next = prev.filter((x) => x.id !== id);
      if (activeProjectId === id) setActiveProjectId(next[0]?.id ?? null);
      return next;
    });
  }

  return (
    <div
      className={[
        'task-page',
        // 목록이 좌측이면 기본(행) 배치, 상단 바 경로는 기존처럼 열 배치.
        TASK_PROJECT_SIDEPANEL ? 'task-page--sidepanel' : 'task-page--topbar',
        TASK_PROJECT_SIDEPANEL && sideCollapsed ? 'is-side-collapsed' : '',
      ].filter(Boolean).join(' ')}
    >
      {/* 2026-07-24 — 레이아웃 SSOT(all-frame.html 3d)대로 프로젝트 목록을 **좌측 2차 패널**로.
          v2.7 의 상단 가로 바는 롤백 플래그(VITE_TASK_PROJECT_SIDEPANEL='0') 경로로 보존. */}
      {TASK_PROJECT_SIDEPANEL ? (
        <ProjectSidePanel
          projects={scopedProjects}
          activeProjectId={activeProjectId}
          onSelect={setActiveProjectId}
          onNew={() => setCreatingProject(true)}
          onDelete={deleteProject}
          onCollapse={() => setSideCollapsed(true)}
          /* 2026-08-07 — teams 는 scope 와 무관하게 **항상** 넘긴다.
             scope 는 프로젝트 로드 뒤 별도 effect 에서 정해지는데, React 는 자식 effect 를
             부모보다 먼저 돌리므로 "projects 는 왔고 scope 는 아직 null" 인 첫 커밋에서
             ProjectSidePanel 의 멤버 조회 effect 가 teams 없이(=권한 필터 myTeamIds 무효)
             먼저 발화해, 내가 팀 멤버가 아닌 팀까지 GET /project-teams/:id/members 를 쳐
             404 TEAM_NOT_FOUND(비-멤버에게 존재를 숨기는 의도된 응답)를 로드마다 찍었다.
             teams 가 아직 비어 있으면 필터가 전부 걸러 요청이 0건 → 도착 후 정상 요청만 나간다.
             스코프 선택기는 `scope && onScopeChange` 조건으로 렌더되므로 UI 는 불변. */
          teams={teams}
          {...(scope ? { scope, onScopeChange: setScope } : {})}
        />
      ) : (
        <ProjectTopBar
          projects={liveProjects}
          activeProjectId={activeProjectId}
          onSelect={setActiveProjectId}
          onNew={() => setCreatingProject(true)}
          onDelete={deleteProject}
        />
      )}

      <main className="task-main">
        {/* 패널이 접혀 있을 때만 나오는 펼치기 버튼(캘린더 사이드바와 같은 방식).
            2026-08-07 — 프로젝트가 선택돼 있으면 헤더 안(제목·설명 왼쪽)으로 옮겨 렌더한다.
            여기 남은 경로는 선택된 프로젝트가 없어 헤더 자체가 없을 때의 폴백 —
            그 상태에서도 접힌 패널을 다시 펼 수 있어야 한다. */}
        {TASK_PROJECT_SIDEPANEL && sideCollapsed && !activeProject && (
          <button
            type="button"
            className="task-side-expand"
            onClick={() => setSideCollapsed(false)}
            aria-label={tTask('task.side.expand')}
            title={tTask('task.side.expand')}
          >
            <ChevronsRight size={14} aria-hidden />
          </button>
        )}
        {!activeProject && projects.length === 0 && (
          <div className="task-empty" role="status">
            <p>{tTask('task.empty_projects_hint')}</p>
          </div>
        )}
        {/* 2026-09-11 — 스코프(스페이스) 안에 프로젝트가 하나도 없을 때. 위 안내는 '프로젝트가 전혀
            없을 때' 만 떠서, 스페이스의 마지막 프로젝트를 지운 직후나 빈 스페이스로 들어오면 헤더도
            보드도 없는 흰 화면이 됐다(QA AXI-PRJ-02-TC008). 프로젝트 홈 링크는 좌측 패널과 같은
            정적 anchor — 이 화면은 Router 없이 렌더되는 계약이다. */}
        {!activeProject && projects.length > 0 && scope !== null && scopedProjects.length === 0 && (
          <div className="task-empty task-empty--scope" role="status">
            <p className="task-empty-title">{tTask('task.empty_scope_title')}</p>
            <p className="task-empty-hint">{tTask('task.empty_scope_hint')}</p>
            <div className="task-empty-actions">
              <button
                type="button"
                className="task-empty-btn task-empty-btn--primary"
                onClick={() => { setCreateFromEmptyScope(true); setCreatingProject(true); }}
              >
                <Plus size={14} aria-hidden />
                {tTask('task.empty_scope_new')}
              </button>
              <a className="task-empty-btn" href="/project">{tTask('task.side.back_home')}</a>
            </div>
          </div>
        )}

        {activeProject && (
          <>
            <header className="task-header">
              {/* 2026-08-07 — 접힌 패널 펼치기(»). 제목·설명 블록 **왼쪽**에 위치.
                  헤더의 첫 flex 아이템으로만 추가되므로 기존 info/actions 배치는 그대로다. */}
              {TASK_PROJECT_SIDEPANEL && sideCollapsed && (
                <button
                  type="button"
                  className="task-side-expand task-side-expand--inheader"
                  onClick={() => setSideCollapsed(false)}
                  aria-label={tTask('task.side.expand')}
                  title={tTask('task.side.expand')}
                >
                  <ChevronsRight size={14} aria-hidden />
                </button>
              )}
              <div className="task-header-info">
                {/* v2.7.1 — 타이틀 뒤 팀 뱃지 (시안 C: 무소속=개인) */}
                <div className="task-title-row">
                  {/* v2.9 — 링크 설정 프로젝트는 타이틀 클릭 → 새 창 (http(s) 만 서버 검증) */}
                  {activeProject.linkUrl ? (
                    <h1 className="task-project-title">
                      <a
                        className="task-project-title-link"
                        href={activeProject.linkUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label={tTask('task.title.link_aria', { name: activeProject.name })}
                        title={activeProject.linkUrl}
                      >
                        {activeProject.name}
                        <ExternalLink size={14} />
                      </a>
                    </h1>
                  ) : (
                    <h1 className="task-project-title">{activeProject.name}</h1>
                  )}
                  <span className="task-team-badge">
                    {activeProject.teamName ?? tTask('task.topbar.personal')}
                  </span>
                </div>
                {activeProject.description && (
                  <p className="task-project-description">{activeProject.description}</p>
                )}
                {/* v2.7.1 (2026-06-11 사용자 요청) — 타이틀 아래 진행률 %·바 제거 */}
              </div>

              <div className="task-header-actions">
                {/* 2026-08-18 시안 ④ — '내 팀' 카드를 헤더의 소형 배지로 접었다.
                    같은 시안에서 종전 팀 이동(👤) 아이콘은 삭제 — 배지 드롭다운의
                    [팀원 초대] 링크가 같은 팀 화면(/project/teams)으로 가므로 진입점이 겹친다. */}
                <TeamMemberBadge teamId={activeProject.teamId ?? null} />
                {/* v2.8.7 의 단일 칸반 뷰 → 2026-08-18 시안대로 툴바에서 목록/칸반 전환. */}
                {/* 화면구성도 V0.1 — 프로젝트 정보(개요·멤버·활동 내역). */}
                <button
                  type="button"
                  className="task-nav-link"
                  aria-label={tProject('project.info.open')}
                  title={tProject('project.info.open')}
                  onClick={() => setInfoOpen(true)}
                >
                  <Info size={15} />
                </button>
                {/* 프로젝트 설정(수정·위임·삭제) — 생성자 본인에게만 노출. */}
                {canOpenSettings && (
                  <button
                    type="button"
                    className="task-nav-link"
                    aria-label={tProject('project.settings.open')}
                    title={tProject('project.settings.open')}
                    onClick={() => setSettingsOpen(true)}
                  >
                    <Settings size={15} />
                  </button>
                )}
                <button
                  type="button"
                  className="task-new-button task-new-button--icononly"
                  aria-label={tTask('task.btn.new')}
                  title={tTask('task.btn.new')}
                  onClick={() => setCreating({ initialStatus: 'TODO' })}
                >
                  <Plus size={16} />
                </button>
              </div>
            </header>

            {error && (
              <div className="task-error" role="alert">
                {error}
                <button type="button" onClick={refreshTasks}>{tTask('task.retry')}</button>
              </div>
            )}

            {/* 2026-08-18 시안 ② — 보기 전환 · 필터 · 업무 검색 한 줄. */}
            <TaskToolbar
              view={view}
              onViewChange={setView}
              filter={filter}
              onFilterChange={setFilter}
              resultCount={shownTasks.length}
            />

            {/* 2026-08-18 시안 ①③ — 보드 영역: 칸반/목록 + 하단 진행현황 도크(접힘 기본).
                종전에 칸반 위에 늘 펼쳐져 있던 TeamGantt 는 도크 안으로 들어갔다(컴포넌트 무수정). */}
            <div className="task-board-area">
              {view === 'kanban' ? (
                <KanbanBoard
                  tasks={shownTasks}
                  loading={loading}
                  onStatusChange={handleStatusChange}
                  onAddInColumn={(status) => setCreating({ initialStatus: status })}
                  onOpenTask={setDetailTask}
                  members={boardMembers}
                  onDeleteTask={(t) => { void deleteOneTask(t); }}
                  canDeleteTask={canDeleteCard}
                />
              ) : (
                <TaskTableView
                  tasks={shownTasks}
                  loading={loading}
                  projectId={activeProject.id}
                  teamId={activeProject.teamId ?? null}
                  onOpenTask={setDetailTask}
                  onTaskUpdated={(updated) => {
                    setTasks((curr) => curr.map((t) => (t.id === updated.id ? updated : t)));
                  }}
                  // 상세의 '추가 정보' 에서 값이 바뀌면 표가 자기 값을 다시 읽는다(2026-08-21).
                  customFieldsVersion={cfVersion}
                />
              )}

              <TeamProgressDock
                tasks={visibleTasks}
                teamId={activeProject?.teamId ?? null}
                onOpenTask={setDetailTask}
              />
            </div>
          </>
        )}
      </main>

      {detailModal.shouldRender && detailModal.latched && (
        <TaskDetailModal
          task={detailModal.latched}
          teamId={activeProject?.teamId ?? null}
          isLeaving={detailModal.isLeaving}
          onClose={() => setDetailTask(null)}
          onSaved={(updated) => {
            setDetailTask(null);
            setTasks((curr) => curr.map((t) => (t.id === updated.id ? updated : t)));
          }}
          onDeleted={() => {
            setDetailTask(null);
            void refreshTasks();
          }}
          // 커스텀 필드 값은 TaskDto 에 없어 onSaved 로는 표에 전해지지 않는다 — 따로 세어 알린다.
          onCustomFieldsChanged={() => setCfVersion((v) => v + 1)}
        />
      )}

      {projectModal.shouldRender && (
        <ProjectCreateSimpleModal
          teams={teams}
          initialTeamId={createFromEmptyScope && scope?.kind === 'team' ? scope.teamId : null}
          isLeaving={projectModal.isLeaving}
          onClose={() => { setCreatingProject(false); setCreateFromEmptyScope(false); }}
          onCreated={(proj) => {
            setCreatingProject(false);
            setCreateFromEmptyScope(false);
            setProjects((p) => [proj, ...p]);
            setActiveProjectId(proj.id);
            // 2026-09-11 — 지금 스코프 밖(다른 스페이스·개인)에 만들었으면 스코프가 따라가게 한다.
            // 그러지 않으면 스코프 선택 effect 가 방금 고른 프로젝트를 도로 걸러 내 빈 화면이 된다.
            if (scope && projectsInScope([proj], scope).length === 0) {
              setScope(proj.teamId ? { kind: 'team', teamId: proj.teamId } : { kind: 'personal' });
            }
          }}
        />
      )}

      {infoOpen && activeProject && (
        <ProjectInfoPanel
          project={activeProject}
          tasks={visibleTasks}
          onClose={() => setInfoOpen(false)}
          onOpenTask={(t) => { setInfoOpen(false); setDetailTask(t); }}
        />
      )}

      {settingsOpen && activeProject && canOpenSettings && (
        <ProjectSettingsModal
          project={activeProject}
          teams={teams}
          onClose={() => setSettingsOpen(false)}
          onSaved={applyProjectPatch}
          onDeleted={onProjectDeletedFromSettings}
        />
      )}

      {shouldRenderCreate && creatingSnap && activeProjectId && (
        <TaskCreateModal
          projectId={activeProjectId}
          teamId={activeProject?.teamId ?? null}
          initialStatus={creatingSnap.initialStatus}
          isLeaving={isCreateLeaving}
          onClose={() => setCreating(null)}
          onCreated={async () => {
            setCreating(null);
            await refreshTasks();
          }}
        />
      )}
    </div>
  );
}
